import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const KEY_PREFIX = "elefa:scroll:";

/**
 * Phase 2 - preserves scroll position per route inside the given scroller.
 * Saves on route change / unmount, restores on entry (POP or revisit).
 */
export function useScrollRestoration(
  scrollerRef: React.RefObject<HTMLElement>,
) {
  const location = useLocation();
  const navType = useNavigationType();
  const lastPath = useRef<string>(location.pathname);

  // Save on path change
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const prevPath = lastPath.current;
    return () => {
      try {
        sessionStorage.setItem(KEY_PREFIX + prevPath, String(el.scrollTop));
      } catch { /* ignore quota */ }
    };
  }, [location.pathname, scrollerRef]);

  // Restore on entry
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let y = 0;
    try {
      const stored = sessionStorage.getItem(KEY_PREFIX + location.pathname);
      // POP or revisit: restore. PUSH: scroll to top.
      if (navType === "POP" && stored) y = parseInt(stored, 10) || 0;
    } catch { /* ignore */ }
    requestAnimationFrame(() => {
      el.scrollTop = y;
    });
    lastPath.current = location.pathname;
  }, [location.pathname, navType, scrollerRef]);
}

export default useScrollRestoration;
